import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ChangeAgentRequest, ChatConfigRequest, ChatConfigResponse, ChatOption, ChatResponse, ChatUserInfo, ImageResponseContent, InitResponse, Message, MessageData, MessageMetadata } from './models/chat.models';
import { ChatService } from './services/chat.service';
import { ChatCommand, ChatEvent, InlineWidget } from './models/event.models';
import { MarkdownService } from 'ngx-markdown';
import { SpeechRecognizer, SpeechSynthesizer, AudioConfig, ResultReason, SpeakerAudioDestination } from "microsoft-cognitiveservices-speech-sdk";
import { ChatResponseType, ChatStatus, FeedbackState, IdleStateMessage, IdleStatusType, IdleTimeOut, LinkType, ReservedTags, Role, SkillType } from './enums/chat.enum';
import { SubscriptionHandlerDirective } from './directives/subscription-handler.directive';
import { ActionType, AgentType, EventType } from './enums/event.enum';
import { VisualizationType } from './enums/widget.enum';
import { DynamicChartRequest, WidgetResponse } from './models/widget.models';
import { ChatEventsService } from './services/chat-events.service';
import { catchError, finalize, map, take, takeWhile, tap } from 'rxjs/operators';
import { EMPTY, Observable, timer } from 'rxjs';
import { SupportData, SupportTicketsContent } from './models/support.models';
import { animate, state, style, transition, trigger } from '@angular/animations';
import * as uuid from 'uuid';
import { ChatInputComponent } from './components/chat-input/chat-input.component';
import { ChatStreamService } from './services/chat-stream.service';
import { LiveAgentConversationEvent, LiveAgentEndEvent, LiveAgentEvent, LiveAgentInitEvent } from './models/live-agent.model';
import { LiveAgentEventType, ZContentType } from './enums/live-agent.enum';
import { FormRequest } from './models/form.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthenticationService } from './services/authentication.service';
import { ChatToggleComponent } from './components/chat-toggle/chat-toggle.component';
import { HostRoute } from './enums/subscription-enum';
import { ConfigurationService } from './services/configuration.service';
import { SubscriptionConfig, SubscriptionRestriction } from './models/subscription.models';
import { AppPermissionService } from './services/app-permission.service';
import { IndexedDbService } from './services/indexed-db.service';
import { environment } from '../../environments/environment';

const DEFAULT_CHAT_AGENT_NAME = "Saffron AI";
const MESSAGE_FIELD = "message";
const FILE_FIELD = "file";
const SITE_ID_FIELD = "siteId";
const ZONE_ID_FIELD = "zoneId";
const MENTIONS_FIELD = "mentions";
const CHAT_PROFILE_FIELD = "profileId";

interface AgentResponseContext {
    response: ChatResponse
    isStreamMessage: boolean
    appendAIMessage: (data: MessageData, messageId?: string) => void
    appendLiveAgentMessage: (data: MessageData, messageId?: string) => void
}


@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    providers: [ChatService, ChatStreamService],
    animations: [
        trigger('fadeInOut', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(500)
            ]),
            transition(':leave', [
                animate(100, style({ opacity: 0 }))
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatComponent extends SubscriptionHandlerDirective implements OnInit {
    @Input() buildingEntityList: any[];
    @Input() isMobileApp: boolean;
    @Input() isCCU: boolean = false;
    @Input() isAndroid: boolean = false;
    @Input() config: ChatConfigRequest;
    @Input() chatUserInfo?: ChatUserInfo;
    @Input() bearerToken: string;
    @Input() subscriptionConfig: SubscriptionConfig;
    @Input() isAIChatSubscriptionActive: boolean = true;
    @Input() sendIconEnabled: boolean = false;
    @Input() isDarkTheme: boolean = false;
    @Input() uploadOptionsEnabled: boolean = true;

    @Output() chatEvent = new EventEmitter<ChatEvent>();
    @Output() close = new EventEmitter();
    @Output() clickSupport = new EventEmitter();
    @Output() minimized = new EventEmitter();
    @Output() maximized = new EventEmitter();
    @Output() expanded = new EventEmitter();
    @Output() defaultView = new EventEmitter();
    @Output() internalLinkRedirection = new EventEmitter<string>();
    @Output() timeoutEvent = new EventEmitter<{ status: IdleStatusType; message: string }>();

    @ViewChild('chatContentElement') private chatContentElement: ElementRef;
    @ViewChild('chatInput') private chatInput: ChatInputComponent;
    @ViewChild('chatToggle') private chatToggle: ChatToggleComponent;

    private _previousScrollHeight: number = 0;

    maximize: boolean = false;
    collapse: boolean = false;

    loading: boolean = false;
    feedbackState: FeedbackState;

    messages: Message[] = [];
    activeMessage: Message;
    activeStreamMessage: Message | null = null;

    initialMessages = [];

    chatInputPlaceholderText: string = "Ask me anything";
    aiAgentName: string = DEFAULT_CHAT_AGENT_NAME;

    showFeedbackPrompt: boolean;

    // Text & Speech options
    speechEnabled: boolean = false;

    chatStatus: ChatStatus = ChatStatus.CHAT_INIT;

    // Live Agent Chat
    chatAgentType: AgentType = AgentType.AI;
    liveAgentName: string;
    liveAgentTypingLoader: boolean = false;
    liveAgentConversationId: string;

    // Mention Options
    mentionOptions: any[] = [];
    resetLastActiveTimeOutInterval: ReturnType<typeof setInterval> | undefined;
    awaitingLiveAgentTime = new Date();
    awaitingReplyTime = new Date();
    IdleState = IdleStatusType;
    disableInput: boolean = false;
    websocketUrl: string;
    chatScreenConfig: ChatConfigResponse;
    imagePreviewOpen: boolean = false;
    cameraDialogOpen: boolean = false;

    constructor(
        private sanitizer: DomSanitizer,
        private chatService: ChatService,
        private markdownService: MarkdownService,
        private configService: ConfigurationService,
        private authenticationService: AuthenticationService,
        private chatEventsService: ChatEventsService,
        private chatStreamService: ChatStreamService,
        private indexedDbService: IndexedDbService,
        private appPermissionService: AppPermissionService
    ) {
        super();
    }

    ngOnInit(): void {
        this.loading = true;
        if (this.bearerToken) {
            this.authenticationService.bearerToken = this.bearerToken;
        }

        this.subs.sink = this.chatService.initializeSession(this.config).pipe(
            tap((response) => {
                this.chatStatus = ChatStatus.CHAT_ACTIVE;
                this.chatService.setSessionId(response.sessionId);
                this.handleAgentResponse(response);
                this.loading = false;
            }),
            tap((response) => {
                this.subs.sink = this.chatStreamService.connect(response.sessionId, this.config.chatUrl).pipe(
                    tap((data) => {
                        const response = data as ChatResponse;
                        this.handleAgentResponse(response);
                    })
                ).subscribe();
            })
        ).subscribe(() => {
            // Set interval to check last active time every one minute.
            this.startLastActiveTimeCheck();
        }, (error: any) => {
            this.loading = false;
            this.handleErrorResponse(error);
        });

        this.subs.sink = this.chatService.getUserChatPreferences().pipe(
            tap((response) => {
                this.aiAgentName = response.agentName;
            })
        ).subscribe();

        this.scrollToBottom();
    }

    get isFacilisightPortal(): boolean {
        return this.configService.getConfig("appName") === HostRoute.FACILISIGHT;
    }

    get activeAgentName(): string {
        return this.chatAgentType == AgentType.AI ? this.aiAgentName : this.liveAgentName;
    }

    get selectedFile(): File | Blob | null {
        return this.chatInput?.selectedFile || this.chatInput?.selectedPhoto;
    };

    connectWebsocket(sessionId: string) {
        this.subs.sink = this.chatStreamService.connect(sessionId, this.config.chatUrl).pipe(
            tap((data) => {
                const response = data as ChatResponse;
                this.handleAgentResponse(response);
            })
        ).subscribe();
    }

    /**
     * Invoked when the user selects an option from the quick select menu.
     * Sets the input text based on the selected option.
     * If the option is vision-related, open the file upload menu.
     * If the option contains an entity reference, also open the mentions menu.
     * Otherwise, auto-submit the message.
     * @param option 
     */
    onSelectOption(option: ChatOption) {
        let placeholderText = option.userInput;
        if (placeholderText.includes(ReservedTags.ENTITY_REF)) {
            placeholderText = placeholderText.replace(ReservedTags.ENTITY_REF, "@");
            setTimeout(() => {
                this.chatInput.openMentionsMenu();
            })
        }
        
        if (option.type == SkillType.LINK) {
            const data = option?.presentationData ?? {};
            switch (data?.linkType) {
                case LinkType.EXTERNAL:
                    this.handleExternalLinkNavigation('quick_option', data.link, data.append_user_info);
                    break;

                case LinkType.INTERNAL:
                    this.handleInternalLinkNavigation(data.link);
                    break;
            }
        } else {
            if (option.presentationData?.openFileUploadMenu) {
                this.chatInput.showUploadOptions = true; // show the file upload menu.
                // opens directly default (ios/android) upload file menu for mobile app.
                if (this.isMobileApp) {
                    this.chatInput.onClickUpload();
                    this.chatInput.quickOptionPrompt = placeholderText;
                } else {
                    this.chatInput.setInputText(placeholderText);
                }
                this.chatInput.showUserInputOptions = false; // hide the user-input options since user has already selected a quick-select prompt.
            } else {
                this.chatInput.setInputText(placeholderText);
                this.chatInput.showUploadOptions = false;
                // auto submit for non-image related quick select options.
                this.onSubmit();
            }
        }
    }

    // Handle the external links naviagtion (Web/Portal)
    handleExternalLinkNavigation(from: string, url: string, appendUserInfo?: boolean) {
        if (appendUserInfo) {
            const userInfoParams = new URLSearchParams();
            const appendIfNotEmpty = (key: string, value?: string) => {
                const normalizedValue = value?.trim();
                if (normalizedValue) {
                    userInfoParams.set(key, normalizedValue);
                }
            };

            appendIfNotEmpty('firstname', this.chatUserInfo?.firstName);
            appendIfNotEmpty('lastname', this.chatUserInfo?.lastName);
            appendIfNotEmpty('email', this.chatUserInfo?.eMail);
            appendIfNotEmpty('phone', this.chatUserInfo?.phone);

            const userInfo = userInfoParams.toString();
            if (userInfo) {
                url += `${url.includes('?') ? '&' : '?'}${userInfo}`;
            }
            
            const pressKitMessage = `I'm interested in learning more about ${environment.config.partnerName} Products`;
            const pressKitMessageParam = new URLSearchParams({ presskitmessage: pressKitMessage }).toString();
            url += `${url.includes('?') ? '&' : '?'}${pressKitMessageParam}`;
            
        }

       

        if (this.isMobileApp || this.isCCU) {
            this.onMobileCitationLinkClicked({
                from: from,
                url
            });
        } else {
            window.open(url, '_blank');
        }
    }

    onToggleAgentType(agentType: AgentType) {
        this.chatStatus = ChatStatus.WAITING_FOR_AGENT;
        this.loading = true;
        const request: ChangeAgentRequest = { profileId: this.config.profileId, siteId: this.config.siteId };
        if (agentType == AgentType.HUMAN) {
            this.awaitingLiveAgentTime = new Date();
        }
        this.startLastActiveTimeCheck(); // setting the interval again to check last active time every one minute. on click of live agent.
        this.chatService.transferAgent(this.chatAgentType, request).pipe(
            tap((response) => {
                this.loading = false;
                if (agentType == AgentType.AI) {
                    // AI agent will be connected immediately
                    this.chatStatus = ChatStatus.CHAT_ACTIVE;
                    this.chatAgentType = AgentType.AI;
                    this.chatStreamService.closeConnection();
                } else {
                    // Wait for live agent to connect. Chat status will be updated to ACTIVE when the live agent sends a message.
                    this.chatStatus = ChatStatus.WAITING_FOR_AGENT;
                    this.ensureActiveWebsocketConnection(response.sessionId);
                }
                this.handleAgentResponse(response);
            })
        ).subscribe();
    }

    /**
     * Ensures that a WebSocket connection is established.
     * If the `connection$` property of the `chatStreamService` is not defined,
     * it initiates a connection to the WebSocket server using the provided URL.
     */
    ensureActiveWebsocketConnection(sessionId: string) {
        if (!this.chatStreamService.connection$) {
            this.connectWebsocket(sessionId);
        }
    }

    onCollapse() {
        this.collapse = true;
        if (this.isMobileApp || this.isCCU) {
            this.minimized.emit();
        }
    }

    onReset() {
        this.maximize = false;
        if (this.isMobileApp || this.isCCU) {
            this.defaultView.emit();
        }
    }

    onExpand() {
        this.collapse = false;
        if (this.isMobileApp) {
            this.expanded.emit();
        } else if (this.isCCU) {
            this.defaultView.emit();
        }
    }

    onMaximize() {
        this.maximize = true;
        if (this.isMobileApp || this.isCCU) {
            this.maximized.emit();
        }
    }

    onCameraDialogOpenChange(isOpen: boolean) {
        this.cameraDialogOpen = isOpen;
    }

    /**
     * Close the chat window and invoke the API to perform session cleanup 
     */
    onClose() {
        this.clearTimeOutIntervals(); // Clearing the intervals on chat close.
        this.chatService.endAIChatSession().subscribe();
        this.close.emit();
        this.indexedDbService.clearIndexedDbStore();
    }

    clearTimeOutIntervals() {
        clearInterval(this.resetLastActiveTimeOutInterval);
    }

    /**
     * Enable feedback tooltip when the user hovers over the AI chat message.
     * Only enabled for AI messages and web applications.
     * @param message AI Message
     */
    onMouseOver(message: Message) {
        if (message.role == Role.AI) {
            message._metadata!.showFeedbackPrompt = true;
        }
    }

    /**
     * Only disable feedback tooltip on mouse-leave if feedback is inactive OR once feedback has been completed.
     * @param message AI Message
     */
    onMouseLeave(message: Message) {
        if ((message._metadata!.feedbackState == FeedbackState.SUBMIT_SENTIMENT || message._metadata!.feedbackState == FeedbackState.CANCEL)) {
            this.closeFeedback(message);
        }
    }

    onFeedbackChange(event: FeedbackState) {
        this.feedbackState = event;
    }

    mentions: any[] = [];
    onMentions(mentions: any[]) {
        this.mentions = mentions;
    }

    onSubmit() {
        const inputText = this.chatInput.getInputText();
        if (inputText.length > 0) {
            this.submitMessage(inputText);
            this.chatInput.resetInput(); // This feels dirty. Maybe we handle this via events instead?
            this.chatInput.resetFileUpload();
        }
    }

    submitMessageToAgent(userMessage: string): Observable<ChatResponse> {
        const formData = new FormData();
        formData.append(MESSAGE_FIELD, userMessage);
        if (this.config?.siteId) {
            formData.append(SITE_ID_FIELD, this.config.siteId);
        }
        if (this.config?.zoneId) {
            formData.append(ZONE_ID_FIELD, this.config.zoneId);
        }
        formData.append(CHAT_PROFILE_FIELD, this.config.profileId);
        this.mentions.forEach(mention => {
            formData.append(MENTIONS_FIELD, JSON.stringify(mention));
        })
        if (this.selectedFile) {
            formData.append(FILE_FIELD, this.selectedFile, (this.selectedFile as File)?.name ?? "capture.png");
        }
        this.addMessage(this.newUserMessage(userMessage, this.selectedFile));

        if (this.chatAgentType == AgentType.AI) {
            return this.chatService.submitMessageToAI(formData);
        } else {
            return this.chatService.submitMessageToHuman(formData, this.liveAgentConversationId);
        }
    }

    submitMessage(userMessage: string) {
        this.loading = true;
        this.startLastActiveTimeCheck(); // setting the interval again to check last active time every one minute.
        this.subs.sink = this.submitMessageToAgent(userMessage).pipe(
            tap((response) => {
                this.handleAgentResponse(response);
                this.chatInput?.resetFileUpload();
                this.loading = false;
            }),
            catchError((err) => {
                this.handleErrorResponse(err);
                this.loading = false;
                return EMPTY;
            })
        ).subscribe();
    }

    closeFeedback(message: Message) {
        (message._metadata as MessageMetadata).showFeedbackPrompt = false;
    }

    onClickHelp() {
        if (this.isMobileApp || this.isCCU) {
            this.clickSupport.emit({ from: 'help', url: this.chatService.chatSupportUrl });
        } else {
            window.open(this.chatService.chatSupportUrl, "_blank");
        }
    }

    // Emit event to open citation link in-app browsers for mobile
    onMobileCitationLinkClicked(event: { from: string; url: string }) {
        this.clickSupport.emit(event);
    }

    ngAfterViewChecked() {
        this.scrollToBottom();
    }

    onToggleSpeech() {
        this.speechEnabled = !this.speechEnabled;
        if (!this.speechEnabled && this.player) {
            this.player.pause();
        }
    }

    onToggleMic() {
        if (this.isMobileApp || this.isCCU) {
            if (this.micEnabled) {
                this.appPermissionService.receiveAppMicrophonePermissionStatus().pipe(take(1)).subscribe((status) => {
                    if(status) {
                        this.recognizeSpeech();
                    } else {
                        this.chatInput.micEnabled = false;
                    }
                });
                this.appPermissionService.requestAppMicrophonePermission().next(true);
                
            } else {
                this.chatInput.micEnabled = false;
            }
        } else {
            if (this.micEnabled) {
                this.recognizeSpeech();
            }
        }
    }

    get micEnabled(): boolean {
        return this.chatInput?.micEnabled ?? false;
    }

    recognizeSpeech() {
        this.subs.sink = this.chatService.speechConfig.subscribe((speechConfig) => {
            const audioConfig = AudioConfig.fromDefaultMicrophoneInput();
            const speechRecognizer = new SpeechRecognizer(speechConfig, audioConfig);

            speechRecognizer.recognizeOnceAsync(result => {
                if (result.reason === ResultReason.RecognizedSpeech) {
                    this.submitMessage(result.text);
                    this.chatInput.resetInput();
                    this.chatInput.resetFileUpload();
                } else {
                    console.error('Speech was cancelled or could not be recognized. Ensure your microphone is working properly.', result);
                }
                speechRecognizer.close();
                this.micDisabled();
            }, err => {
                this.micDisabled();

            });
        }, err => {
            this.micDisabled();
        })
    }

    micDisabled() {
        this.chatInput.micEnabled = false;
    }

    player: SpeakerAudioDestination;
    speechSynthesizer: SpeechSynthesizer;
    synthesizeSpeech(ai_response: string, language: string) {
        //If the player is already playing, pause it.
        if (this.player && this.speechSynthesizer) {
            this.player.pause();
        }
        this.player = new SpeakerAudioDestination();
        const audioConfig = AudioConfig.fromSpeakerOutput(this.player);
        this.subs.sink = this.chatService.speechConfig.subscribe((speechConfig) => {
            speechConfig.speechSynthesisLanguage = language;
            this.speechSynthesizer = new SpeechSynthesizer(speechConfig, audioConfig);
            this.speechSynthesizer.speakTextAsync(ai_response, result => {
                if (result) {
                    this.speechSynthesizer.close();
                }
            },
                error => {
                    console.error(error);
                    this.speechSynthesizer.close();
                })
        })
    }

    /**
     * Determines if a message can prompt the user for feedback.
     * @param message Chat message
     * @returns boolean
     */
    canShowFeedback(message: Message) {
        return message._metadata!.showFeedbackPrompt && message.role !== Role.LIVE_AGENT && !this.isMobileApp;
    }

    /**
     * Opens image in a new tab
     * @param imageUrl 
     */
    openImageInNewTab(imageUrl: string | SafeUrl) {
        if (typeof imageUrl === 'object') {
            window.open(this.sanitizer.sanitize(4, imageUrl) || '', '_blank');
        } else {
            window.open(imageUrl, '_blank');
        }
    }
    /**
     * Sets an interval for idle check.
     * 
     * @param idleTime - The idle time in milliseconds.
     * @param checkTime - A function that returns the last active time.
     * @param idleStatusType - The type of idle status.
     * @returns The interval ID.
     */
    startIdleCheck(idleTime: number, checkTime: () => number, idleStatusType: IdleStatusType) {
        return setInterval(() => {
            const lastActiveTime = checkTime();
            if ((this.currentTime - lastActiveTime) >= idleTime) {
                this.getIdleStateMessage(idleStatusType);
            }
        }, 300);
    }

    /**
     * Sets an interval for updating the last active time.
     * This method clears the previous interval and sets a new one.
     * The interval is used to periodically check if the user is idle.
     * If the user is idle, it triggers the idle status type and performs a specific action.
     */
    startLastActiveTimeCheck() {
        clearInterval(this.resetLastActiveTimeOutInterval);
        this.resetLastActiveTimeOutInterval = this.startIdleCheck(
            IdleTimeOut.USER_IDLE,
            () => new Date(this.chatService.lastInteractedDatetime as string).getTime(),
            IdleStatusType.USER_IDLE
        );
    }

    /**
     * Retrieves the idle state message based on the provided idle state.
     * 
     * @param idleState - The idle state type.
     */
    getIdleStateMessage(idleState: IdleStatusType) {
        let textContent = IdleStateMessage[idleState] as string;
        clearInterval(this.resetLastActiveTimeOutInterval); // Clearing the interval on idle state, if the user is idle or agent is idle.
        this.timeoutEvent.emit({ status: idleState, message: textContent });
        this.addMessage(this.newAIMessage({ text: textContent }));
        this.disableInput = true;
    }

    cancelLiveAgentConnection() {
        this.chatAgentType = AgentType.AI; // changing the agent type to AI.
        this.chatStatus = ChatStatus.CHAT_ACTIVE; // changing the chat status to active.
        this.chatService.endAIChatSession().subscribe(); // ending the live agent chat session.
        this.chatToggle.onToggle(true); // changing the agent type to AI.
    }

    // Method to restrict the background scroll when scroll reaches the top or bottom of the chat window.
    onScroll(event: WheelEvent): void {

        // Allow scroll within horizontal scroll containers (e.g., resource cards container)
        const horizontalScrollTarget = (event.target as HTMLElement).closest(
            '.resource-cards-container'  // your horizontal scroll container selector
        );
        if (horizontalScrollTarget) return

        const chatContent = this.chatContentElement.nativeElement;
        const scrollMargin = 2;
        // Check if the user is at the top or bottom of the chat-messages
        const isAtBottom = chatContent.scrollHeight - chatContent.scrollTop - chatContent.clientHeight <= scrollMargin;
        const isAtTop = chatContent.scrollTop <= scrollMargin;

        if ((isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0)) {
            // Prevent background scroll when reached top or bottom of the chat window
            event.preventDefault();
        } else {
            chatContent.scrollTop += event.deltaY; // Ensure the scroll is contained within chat window
            event.preventDefault(); // Preventing the event propagation
        }
    }

    // Method to restrict the background scroll when scroll reaches the top or bottom of the chat window for mobile.
    onTouchMove(event: TouchEvent): void {

        // Let horizontal scroll containers handle their own touch scroll (e.g., resource cards container)
        const horizontalScrollTarget = (event.target as HTMLElement).closest(
            '.resource-cards-container'
        );
        if (horizontalScrollTarget) return

        const chatContent = this.chatContentElement.nativeElement;
        const scrollMargin = 2;
        const isAtBottom = chatContent.scrollHeight - chatContent.scrollTop - chatContent.clientHeight <= scrollMargin;
        const isAtTop = chatContent.scrollTop <= scrollMargin;

        const touchStart = event.touches[0].clientY;
        const touchEnd = event.changedTouches[0]?.clientY || touchStart;
        const touchDirection = touchStart - touchEnd;
        if ((isAtTop && touchDirection < 0) || (isAtBottom && touchDirection > 0)) {
            event.preventDefault();
        } else {
            chatContent.scrollTop += touchDirection;
        }
    }

    // Method to restrict the scroll behavior of the chat container.
    onContainerScroll(event: Event) {
        const target = event.target as HTMLElement;

        // Check if the target is not the chat-messages and mention-item list to restrict the scroll outside the chat-messages
        if (!target.closest('.chat-messages') && !target.closest('.mention-item')) {
            event.preventDefault();
        }
    }

    private handleAgentResponse(response: ChatResponse): any {
        /**
         * The agent responds to most queries in markdown.
         * However, certain responses such as visualization tasks yield stringified JSON responses.
         * Thus, the response may be in JSON or markdown format.
         * We first attempt to parse JSON. In the event that the parsing fails, we then parse to plain-text. 
        */
        if (response == null) {
            return;
        }
        if (response?.config) {
            this.chatScreenConfig = response.config as ChatConfigResponse;
        }
        const context = this.buildResponseContext(response);

        switch (response.responseType) {
            case ChatResponseType.VISUALIZATION:
                this.handleVisualization(context);
                break;
            case ChatResponseType.SUPPORT:
                this.appendSupportMessage(context);
                break;
            case ChatResponseType.FORM_INPUT:
                context.appendAIMessage({ formRequest: response.content as FormRequest });
                break;
            case ChatResponseType.LIVE_AGENT_EVENT:
                this.handleLiveAgentResponse(context, response.content as LiveAgentEvent);
                break;
            case ChatResponseType.ERROR:
                this.appendErrorMessage(context);
                break;
            case ChatResponseType.IMAGE:
                context.appendAIMessage({ imageData: response.content as ImageResponseContent });
                break;
            case ChatResponseType.SUPPORT_TICKETS:
                context.appendAIMessage({ supportTicketsContent: response.content as SupportTicketsContent });
                break;
            case ChatResponseType.SUBSCRIPTION_RESTRICTION:
                context.appendAIMessage({ subscriptionData: response.content as SubscriptionRestriction });
                break;
            default:
                this.appendTextMessage(context);
                break;
        }
    }

    private async appendSupportMessage(context: AgentResponseContext) {
        const response = context.response;
        const supportResponse = response.content as SupportData;
        const markdown: any = await this.markdownService.parse(supportResponse.content);
        const supportData: SupportData = {
            ...supportResponse,
            content: markdown
        };

        context.appendAIMessage({ supportData });
    }

    private async appendErrorMessage(context: AgentResponseContext) {
        const response = context.response;
        const errorMessage = response.content as string;
        if (this.speechEnabled) {
            this.synthesizeSpeech(errorMessage, response.language);
        }
        const markdown: any = await this.markdownService.parse(errorMessage);
        context.appendAIMessage({ error: markdown });
    }

    private async appendTextMessage(context: AgentResponseContext) {
        const response = context.response;
        const textContent = response.content as string;
        const markdown: any = await this.markdownService.parse(textContent);
        context.appendAIMessage({ text: markdown, quickSelectOptions: response.quickSelectOptions });

        if (this.speechEnabled) {
            this.synthesizeSpeech(textContent, response.language);
        }
        
        if (!this.isMobileApp) {
            this.setInputCursor();
        }
    }

    private buildResponseContext(response: ChatResponse): AgentResponseContext {
        const isStreamMessage = (response as any).stream === true;
        
        return {
            response,
            isStreamMessage,
            appendAIMessage: (data: MessageData, messageId: string = response.messageId) => {
                this.addMessage(this.newAIMessage(data, messageId), isStreamMessage);
            },
            appendLiveAgentMessage: (data: MessageData, messageId: string = response.messageId) => {
                this.addMessage(this.newLiveAgentMessage(data, messageId), isStreamMessage);
            }
        };
    }

    get showUserInputOptions(): boolean {
        return this.chatAgentType === AgentType.AI
    }
    
    isActiveStreamMessage(message: Message): boolean {
        return !!message._metadata.isStreamMessage && this.activeMessage === message;
    }

    private addMessage(message: Message, isStreamMessage: boolean = false) {
        message._metadata.isStreamMessage = isStreamMessage;
        this.activeMessage = message;
        this.messages.push(message);
    }

    private setInputCursor() {
        this.chatInput?.setInputCursor();
    }

    private async handleVisualization(context: AgentResponseContext) {
        const response = context.response;
        const widgetResponse = response.content as WidgetResponse;
        let textContent: string = "";

        switch (widgetResponse.eventType) {
            case EventType.COMMAND:
                const command: ChatCommand = widgetResponse as ChatCommand;
                switch (command.actionType) {
                    case ActionType.APPLY_HEATMAP_OVERLAY:
                        textContent = widgetResponse.explanation ?? "Applying a heatmap overlay...";
                        context.appendAIMessage({ text: textContent });
                        break;
                    case ActionType.ADD_SUMMARY_TABLE:
                        textContent = widgetResponse.explanation ?? "Adding a Summary Table..."
                        context.appendAIMessage({ text: textContent });
                        break;
                    case ActionType.NEW_DASHBOARD_NAVIGATE:
                        await this.updateCountdownMessage(widgetResponse, context);
                        break;
                    default:
                        textContent = "I don't recognize that action";
                        context.appendAIMessage({ text: textContent });
                        break;
                }
                this.chatEventsService.broadcastEvent(command);
                break;
            case EventType.INLINE_VISUALIZATION:
                const inlineWidget: InlineWidget = widgetResponse as InlineWidget;
                if (inlineWidget.visualizationType == VisualizationType.DYNAMIC_CHART) {
                    const dynamicChartRequest: DynamicChartRequest = {
                        data: inlineWidget.data.data,
                        config: inlineWidget.data.config
                    }
                    textContent = dynamicChartRequest.config.explanation;
                    context.appendAIMessage({ text: textContent });
                    context.appendAIMessage({ widgetData: { visualizationType: VisualizationType.DYNAMIC_CHART, data: dynamicChartRequest } });
                } else {
                    console.error("Unrecognized visualization type", widgetResponse);
                }
                break;
            default:
                console.error("Unrecognized event type", widgetResponse);
        }
        if (this.speechEnabled && textContent.length > 0) {
            this.synthesizeSpeech(textContent, response.language);
        }
    }

    private handleLiveAgentResponse(context: AgentResponseContext, response: LiveAgentEvent) {
        // Live Agent Chat
        if (response.type == LiveAgentEventType.CONVERSATION_CREATE) {
            const initEvent = response as LiveAgentInitEvent;
            this.chatStatus = ChatStatus.WAITING_FOR_AGENT;
            this.liveAgentConversationId = initEvent.zendeskConversationId;
            // This response is still from the AI agent
            context.appendAIMessage({ text: initEvent.text });
        }
        else if (response.type == LiveAgentEventType.CONVERSATION_TYPING) {
            this.liveAgentTypingLoader = true;
            this.chatAgentType = AgentType.HUMAN;
            this.chatStatus = ChatStatus.CHAT_ACTIVE;
        }
        else if (response.type == LiveAgentEventType.CONVERSATION_MESSAGE) {
            const conversationEvent = response as LiveAgentConversationEvent;
            this.chatStatus = ChatStatus.CHAT_ACTIVE;
            this.chatAgentType = AgentType.HUMAN;
            this.liveAgentTypingLoader = false;
            this.liveAgentName = conversationEvent.agentName;
            this.awaitingReplyTime = new Date();
            if (conversationEvent.content.type == ZContentType.IMAGE) {
                context.appendLiveAgentMessage({ mediaUrl: conversationEvent.content.mediaUrl });
            } else {
                context.appendLiveAgentMessage({ text: conversationEvent.content.text });
            }
        } else if (response.type == LiveAgentEventType.CONVERSATION_END) {
            const endEvent = response as LiveAgentEndEvent;
            this.chatStatus = ChatStatus.ENDING_CHAT;
            // Hand off to AI
            this.chatAgentType = AgentType.AI;
            context.appendAIMessage({ text: endEvent.text });
        } else {
            console.error("Unrecognized live-agent event", response);
        }
    }

    private handleErrorResponse(error: any): void {
        this.addMessage(this.newAIMessage({ text: error?.error?.detail ?? "Sorry! Please try again." }));
    }

    private newUserMessage(text: string, file?: Blob | null, messageId: string = uuid.v4()): Message {
        let data: MessageData = { text: text };
        if (file?.type.startsWith("image")) {
            const mediaUrl = URL.createObjectURL(file);
            data.mediaUrl = this.sanitizer.bypassSecurityTrustUrl(mediaUrl);
        } else if (file) {
            data.file = file as File;
        }
        return { role: Role.USER, data: data, _metadata: { messageId: messageId, showFeedbackPrompt: false } }
    }

    private newAIMessage(data: MessageData, messageId: string = uuid.v4()): Message {
        const message = { role: Role.AI, data: { ...data }, _metadata: { messageId: messageId, showFeedbackPrompt: false } }
        return message;
    }

    private newLiveAgentMessage(data: MessageData, messageId: string = uuid.v4()): Message {
        const message = { role: Role.LIVE_AGENT, data: { ...data }, _metadata: { messageId: messageId, showFeedbackPrompt: false } }
        return message;
    }

    private scrollToBottom(): void {
        const element = this.chatContentElement?.nativeElement;
        if (element != null && element.scrollHeight > this._previousScrollHeight) {
            this._previousScrollHeight = element.scrollHeight;
            element.scrollTop = element.scrollHeight;
        }
    }

    private get currentTime(): number {
        return new Date().getTime();
    }


    /**
     * Updates a chat message with a countdown timer, displaying dynamic content as the countdown progresses.
     * 
     * - Parses and displays the initial message with the countdown duration.
     * - Updates the message every second to reflect the remaining time.
     * - On countdown completion, updates the message with a final text.
     * - Message object contains list of messages:
     *   - messages[0]: Display chat message [Ex: ⚙️ Analyzing your image to generate your Dashboard. ]
     *   - messages[1]: Sub message during countdown [Ex: You’ll be redirected to the dashboard in 5 Seconds.]
     *   - messages[2]: Final Sub message after countdown [Ex: Preparing your dashboard builder…]
     * 
     * @param widgetResponse - The response object containing countdown configuration and message templates.
     * @param context - Context containing message metadata and append helpers for the current response.
     * @returns A promise that resolves when the countdown is complete and the final message is displayed.
     */
    async updateCountdownMessage(widgetResponse: WidgetResponse, context: AgentResponseContext) {
        const messageId = context.response.messageId;
        const unit = widgetResponse.countdown?.unit || "seconds";
        let duration = this.getDurationInSeconds(widgetResponse.countdown?.duration || 0, unit);
        const messages = widgetResponse.messages || [];

        let textContent = messages?.length
            ? `${messages[0]}\n\n${messages[1]} **${duration} ${unit}**`
            : "Navigating to the new dashboard in a few seconds…";
        const markdown: any = await this.markdownService.parse(textContent);
        context.appendAIMessage({ text: markdown });

        // Countdown
        await new Promise<void>((resolve) => {
            timer(0, 1000).pipe(
                map(i => duration - i),
                takeWhile(remaining => remaining > 0),
                finalize(() => {
                    const finalText = `${messages[0]}\n\n${messages[2]}`;
                    this.updateChatMessage(finalText, messageId);
                    resolve();
                })
            ).subscribe((remaining : number) => {
                const countdownText = `${messages[0]}\n\n${messages[1]} **${remaining} ${unit}.**`;
                this.updateChatMessage(countdownText, messageId);
            });
        });
    };

    private async updateChatMessage(textContent: string, messageId: string) {
        const messageIndex = this.messages.findIndex(msg => msg._metadata?.messageId === messageId);
        const markdown: any = await this.markdownService.parse(textContent);
        this.messages[messageIndex].data.text = markdown;
    }

    getDurationInSeconds(duration: number, unit: string): number {
        switch (unit.toLowerCase()) {
            case "minutes": return duration * 60;
            case "hours": return duration * 3600;
            default: return duration;
    }
}

   /**
     * Method to handle the internal link navigation (the pages within the application).
     * @param url - the url from API response.
     */
    handleInternalLinkNavigation(url: string) {
        if (!url) {
            return;
        }
        this.internalLinkRedirection.emit(url);
    }
}
